'use client';

import React, { useEffect, useRef } from 'react';

interface StatBlock {
  number: string;
  unit?: string;
  label: string;
  context: string;
  subContext?: string;
  accent: 'sky' | 'sage' | 'slate';
}

const STATS: StatBlock[] = [
  {
    number: '48',
    unit: 'hours',
    label: 'Average funding decision',
    context: 'While traditional banks can take weeks to review a loan application, our streamlined process connects your profile to the right lending programs instantly — so decisions come back in hours, not months.',
    subContext: 'A small business owner in Dallas received a $120,000 term loan approval within 36 hours of submitting her application through Lenders Home.',
    accent: 'sky',
  },
  {
    number: '$2.4B',
    label: 'In loans facilitated',
    context: 'Since our founding, Lenders Home LLC has helped connect borrowers with over $2.4 billion in funded loans across business, personal, and healthcare lending programs — serving clients in all 50 states.',
    subContext: 'From a $8,000 personal loan for medical expenses to a $3.2M commercial real estate loan, our network covers the full spectrum of borrower needs.',
    accent: 'sage',
  },
  {
    number: '200+',
    unit: 'programs',
    label: 'Lending programs available',
    context: 'We cross-reference your financial profile against more than 200 distinct lending programs — including SBA loans, term loans, lines of credit, merchant cash advances, and specialized healthcare financing.',
    subContext: 'A restaurant owner in Miami who was declined by three banks qualified for an SBA 7(a) loan through a program he had never heard of.',
    accent: 'sky',
  },
];

export default function StatsCascade() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-white overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="text-center mb-20 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-light border border-sky/20 text-xs font-semibold text-sky-dark font-body uppercase tracking-wide mb-5">
            The Numbers
          </span>
          <h2 className="font-heading font-bold text-display text-slate">
            The data speaks for itself.
          </h2>
          <p className="font-body text-slate-fog text-lg mt-3 max-w-xl mx-auto">
            Every number below represents real outcomes for real borrowers. The programs exist — you just need the right partner to find them.
          </p>
        </div>

        {/* Stats rows */}
        <div className="space-y-24 lg:space-y-32">
          {STATS.map((stat, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <div
                key={stat.number}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}
              >
                {/* Stat number side */}
                <div className={`${isEven ? 'reveal-left' : 'reveal-right lg:col-start-2'}`}>
                  <div className="relative">
                    <div
                      className={`absolute -top-8 -left-8 w-48 h-48 rounded-full blur-[60px] pointer-events-none ${
                        stat.accent === 'sky' ? 'bg-sky/10' : stat.accent === 'sage' ? 'bg-sage/15' : 'bg-slate/8'
                      }`}
                    />
                    <div className="relative">
                      <div className="font-heading font-black text-stat tracking-tight leading-none mb-3 stat-number">
                        {stat.number}
                        {stat.unit && (
                          <span
                            className={`text-3xl lg:text-5xl ml-2 font-bold ${
                              stat.accent === 'sky' ? 'text-sky' : stat.accent === 'sage' ? 'text-sage' : 'text-slate-light'
                            }`}
                          >
                            {stat.unit}
                          </span>
                        )}
                      </div>
                      <p className="font-heading font-bold text-xl lg:text-2xl text-slate tracking-tight">
                        {stat.label}
                      </p>
                      <div
                        className={`mt-4 w-16 h-1 rounded-full ${
                          stat.accent === 'sky' ? 'bg-sky' : stat.accent === 'sage' ? 'bg-sage' : 'bg-slate-light'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Context side */}
                <div className={`${isEven ? 'reveal-right' : 'reveal-left lg:col-start-1 lg:row-start-1'}`}>
                  <p className="font-body text-slate-light text-lg leading-relaxed mb-6">
                    {stat.context}
                  </p>
                  {stat.subContext && (
                    <div className="border-l-2 border-sky pl-5 py-1">
                      <p className="font-body text-sm text-slate-fog italic leading-relaxed">
                        {stat.subContext}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}